import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Star, StarHalf, MessageSquare, TrendingUp, Award,
  Users, Filter, Search, RefreshCw, Download,
  ThumbsUp, ThumbsDown, Calendar, User
} from 'lucide-react';
import { api } from '@/lib/api';
import { ExportButton } from '@/components/ExportButton';

interface Rating {
  id: number;
  shipmentId: number;
  trackingNumber: string | null;
  customerId: number;
  customerName: string | null;
  delegateId: number | null;
  delegateName: string | null;
  ratingValue: number;
  comment: string | null;
  category: string;
  responseTime: number | null;
  deliverySpeed: number | null;
  customerService: number | null;
  overallSatisfaction: number | null;
  isAnonymous: boolean;
  status: string;
  adminResponse: string | null;
  adminRespondedAt: string | null;
  createdAt: string;
}

interface Stats {
  total: number;
  average: number;
  distribution: { rating: number; count: number }[];
  byCategory: { category: string; count: number; avgRating: number }[];
  recent: { id: number; ratingValue: number; comment: string | null; createdAt: string }[];
}

interface DelegateRanking {
  delegateId: number;
  delegateName: string;
  totalRatings: number;
  averageRating: number;
  fiveStars: number;
  fourStars: number;
  threeStars: number;
  twoStars: number;
  oneStar: number;
}

export default function RatingsPage() {
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [ranking, setRanking] = useState<DelegateRanking[]>([]);
  const [loading, setLoading] = useState(false);
  const [minRating, setMinRating] = useState<number | null>(null);
  const [maxRating, setMaxRating] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (minRating) params.append('minRating', String(minRating));
      if (maxRating) params.append('maxRating', String(maxRating));

      const [ratingsRes, statsRes, rankingRes] = await Promise.all([
        api.get(`/api/ratings?${params.toString()}`),
        api.get('/api/ratings/stats'),
        api.get('/api/ratings/delegates-ranking?top=10')
      ]);

      if (ratingsRes.data.success) setRatings(ratingsRes.data.data.items);
      if (statsRes.data.success) setStats(statsRes.data.data);
      if (rankingRes.data.success) setRanking(rankingRes.data.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
      } else {
        stars.push(<Star key={i} className="w-4 h-4 text-gray-300" />);
      }
    }
    return <div className="flex gap-0.5">{stars}</div>;
  };

  const filteredRatings = ratings.filter(r => {
    if (search && !r.comment?.toLowerCase().includes(search.toLowerCase()) && 
        !r.customerName?.toLowerCase().includes(search.toLowerCase()) &&
        !r.trackingNumber?.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    return true;
  });

  const exportColumns = [
    { key: 'trackingNumber', label: 'رقم التتبع' },
    { key: 'customerName', label: 'العميل' },
    { key: 'delegateName', label: 'المندوب' },
    { key: 'ratingValue', label: 'التقييم' },
    { key: 'comment', label: 'التعليق' },
    { key: 'category', label: 'الفئة' },
    { key: 'createdAt', label: 'التاريخ', format: (v: string) => new Date(v).toLocaleDateString('ar-EG') }
  ];

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center">
              <Star className="w-6 h-6 text-white" />
            </div>
            نظام التقييمات
          </h1>
          <p className="text-muted-foreground mt-1">إدارة ومتابعة تقييمات العملاء</p>
        </div>
        <div className="flex gap-2">
          <ExportButton data={ratings} filename="ratings" columns={exportColumns} title="التقييمات" />
          <Button onClick={loadData} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ml-2 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-yellow-50 to-orange-100 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700">إجمالي التقييمات</p>
                  <p className="text-2xl font-bold text-yellow-900">{stats.total}</p>
                </div>
                <Star className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">متوسط التقييم</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.average}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">تقييمات 5 نجوم</p>
                  <p className="text-2xl font-bold text-green-900">
                    {stats.distribution.find(d => d.rating === 5)?.count || 0}
                  </p>
                </div>
                <ThumbsUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700">تقييمات 1-2 نجمة</p>
                  <p className="text-2xl font-bold text-red-900">
                    {(stats.distribution.find(d => d.rating === 1)?.count || 0) + 
                     (stats.distribution.find(d => d.rating === 2)?.count || 0)}
                  </p>
                </div>
                <ThumbsDown className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rating Distribution */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle>توزيع التقييمات</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[5, 4, 3, 2, 1].map(rating => {
                const count = stats.distribution.find(d => d.rating === rating)?.count || 0;
                const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
                return (
                  <div key={rating} className="flex items-center gap-3">
                    <div className="flex gap-0.5 w-24">
                      {renderStars(rating)}
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-6 overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          rating >= 4 ? 'bg-green-500' : 
                          rating === 3 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold w-16 text-left">{count} ({percentage.toFixed(1)}%)</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="بحث في التعليقات..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pr-10"
              />
            </div>
            <select
              value={minRating ?? ''}
              onChange={(e) => setMinRating(e.target.value ? Number(e.target.value) : null)}
              className="px-4 py-2 border rounded-lg bg-white"
            >
              <option value="">أقل تقييم</option>
              <option value="1">1 نجمة</option>
              <option value="2">2 نجمتين</option>
              <option value="3">3 نجوم</option>
              <option value="4">4 نجوم</option>
              <option value="5">5 نجوم</option>
            </select>
            <select
              value={maxRating ?? ''}
              onChange={(e) => setMaxRating(e.target.value ? Number(e.target.value) : null)}
              className="px-4 py-2 border rounded-lg bg-white"
            >
              <option value="">أعلى تقييم</option>
              <option value="1">1 نجمة</option>
              <option value="2">2 نجمتين</option>
              <option value="3">3 نجوم</option>
              <option value="4">4 نجوم</option>
              <option value="5">5 نجوم</option>
            </select>
            <Button onClick={loadData} variant="outline">
              <Filter className="w-4 h-4 ml-2" />
              تطبيق
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Ratings List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>قائمة التقييمات</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
                <p>جاري التحميل...</p>
              </div>
            ) : filteredRatings.length === 0 ? (
              <div className="text-center py-12">
                <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">لا توجد تقييمات</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredRatings.map((rating) => (
                  <div key={rating.id} className="p-4 hover:bg-gray-50 transition">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                        {rating.ratingValue}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {renderStars(rating.ratingValue)}
                          <Badge variant="outline">{rating.category}</Badge>
                          {rating.isAnonymous && <Badge variant="secondary">مجهول</Badge>}
                        </div>
                        {rating.comment && (
                          <p className="text-sm text-gray-700 mb-2">{rating.comment}</p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                          {rating.trackingNumber && (
                            <span>شحنة: {rating.trackingNumber}</span>
                          )}
                          {rating.customerName && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {rating.customerName}
                            </span>
                          )}
                          {rating.delegateName && (
                            <span>المندوب: {rating.delegateName}</span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(rating.createdAt).toLocaleDateString('ar-EG')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delegates Ranking */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              أفضل المندوبين
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ranking.length === 0 ? (
              <div className="text-center py-8">
                <Award className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">لا يوجد مندوبين بعد</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ranking.map((delegate, index) => (
                  <div key={delegate.delegateId} className="p-3 rounded-lg hover:bg-gray-50 transition">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-600 text-white' :
                        index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                        index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{delegate.delegateName}</p>
                        <p className="text-xs text-gray-500">{delegate.totalRatings} تقييم</p>
                      </div>
                      <div className="text-left">
                        <p className="text-lg font-bold text-yellow-600">{delegate.averageRating}</p>
                        <div className="flex gap-0.5">
                          {renderStars(Math.round(delegate.averageRating))}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 text-xs">
                      <span className="text-green-600">5★:{delegate.fiveStars}</span>
                      <span className="text-blue-600">4★:{delegate.fourStars}</span>
                      <span className="text-yellow-600">3★:{delegate.threeStars}</span>
                      <span className="text-orange-600">2★:{delegate.twoStars}</span>
                      <span className="text-red-600">1★:{delegate.oneStar}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
